import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Shield, Truck, Star, ChevronRight, Loader2, X, ZoomIn } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useProduct, useProducts } from '../hooks/useProducts';
import { formatPrice, getProductImageUrl } from '../lib/utils';
import AddToCartModal from '../components/AddToCartModal';
import ProductReviewsSection from '../components/ProductReviewsSection';
import ProductRecommendations from '../components/ProductRecommendations';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addItem } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  
  // Fetch product data from Supabase
  const { product, loading, error } = useProduct(id || '');
  
  // Fetch related products (same category, excluding current product)
  const { products: relatedProducts } = useProducts({ 
    category: product?.category,
    limit: 4
  });
  
  // Filter out current product from related products
  const filteredRelatedProducts = relatedProducts.filter(p => p.id !== product?.id);
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Update document title
    if (product) {
      document.title = `${product.name} | Strum Music`;
    }
  }, [product]);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isImageModalOpen) {
        setIsImageModalOpen(false);
      }
    };

    if (isImageModalOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isImageModalOpen]);
  
  const handleAddToCart = () => {
    if (!product) return;
    
    // Convert database product to cart product format
    const cartProduct = {
      id: product.id,
      name: product.name,
      category: product.category,
      subcategory: product.subcategory,
      price: product.price,
      discountPrice: undefined, // No discount_price in current schema
      rating: product.rating,
      image: getProductImageUrl(product.image_url),
      images: product.image_url ? [getProductImageUrl(product.image_url)] : [],
      description: product.description,
      features: product.features,
      specs: product.specifications,
      stock: product.quantity,
      isNew: product.is_new,
      isFeatured: product.is_featured,
      productType: product.product_type
    };
    
    addItem(cartProduct, quantity);
    setShowCartModal(true);
  };

  // Handle modal overlay click
  const handleModalOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking the overlay itself, not the content
    if (e.target === e.currentTarget) {
      setIsImageModalOpen(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white min-h-screen">
        <div className="container-custom py-16">
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-red-700 mr-3" />
            <span className="text-gray-600">Loading product details...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <div className="bg-white min-h-screen">
        <div className="container-custom py-16 text-center">
          <h1 className="text-2xl font-bold mb-4">Product Not Found</h1>
          <p className="mb-6 text-gray-600">
            {error ? `Error: ${error}` : "Sorry, the product you're looking for doesn't exist."}
          </p>
          <Link to="/products" className="btn btn-primary px-6 py-2">
            Return to Shop
          </Link>
        </div>
      </div>
    );
  }

  const productImages = product.image_url ? [getProductImageUrl(product.image_url)] : [];

  return (
    <div className="bg-white">
      {/* Breadcrumbs */}
      <div className="bg-gray-50 py-3 border-b border-gray-100">
        <div className="container-custom">
          <nav className="flex text-sm">
            <Link to="/" className="text-gray-500 hover:text-red-700">Home</Link>
            <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
            <Link to="/products" className="text-gray-500 hover:text-red-700">Products</Link>
            <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
            <Link to={`/products/${product.category}`} className="text-gray-500 hover:text-red-700 capitalize">
              {product.category_info?.name || product.category}
            </Link>
            <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
            <span className="text-gray-700 font-medium truncate">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Details */}
      <div className="container-custom py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {/* Product Images */}
          <motion.div 
            className="space-y-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Main Image Container - Fixed to show complete image */}
            <div className="w-full h-[500px] rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 overflow-hidden relative group cursor-pointer"
                 onClick={() => setIsImageModalOpen(true)}>
              <div className="w-full h-full flex items-center justify-center p-6">
                <img 
                  src={productImages[selectedImage] || productImages[0] || getProductImageUrl(null)} 
                  alt={product.name} 
                  className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105"
                  onError={(e) => {
                    e.currentTarget.src = getProductImageUrl(null);
                  }}
                />
              </div>
              
              {/* Zoom Indicator */}
              <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <ZoomIn className="h-4 w-4" />
              </div>
            </div>
            
            {/* Thumbnail Gallery */}
            {productImages.length > 1 && (
              <div className="flex space-x-2 overflow-auto pb-2 snap-x">
                {productImages.map((image, i) => (
                  <button 
                    key={i} 
                    onClick={() => setSelectedImage(i)} 
                    className={`snap-start shrink-0 rounded border-2 overflow-hidden ${
                      selectedImage === i ? 'border-red-500' : 'border-transparent'
                    }`}
                  >
                    <div className="w-16 h-16 bg-gray-100 flex items-center justify-center p-1">
                      <img 
                        src={image} 
                        alt={`${product.name} view ${i + 1}`} 
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
          
          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Status indicators */}
            <div className="flex flex-wrap gap-2 mb-3">
              {product.is_new && (
                <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">New</span>
              )}
              {product.is_featured && (
                <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded">Featured</span>
              )}
              <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded">
                In Stock: {product.quantity}
              </span>
              {product.product_type !== 'regular' && (
                <span className={`text-white text-xs font-medium px-2.5 py-0.5 rounded ${
                  product.product_type === 'rental' ? 'bg-blue-600' :
                  product.product_type === 'open-box' ? 'bg-green-600' :
                  'bg-purple-600'
                }`}>
                  {product.product_type === 'rental' ? 'Rental' :
                   product.product_type === 'open-box' ? 'Open Box' :
                   'Factory Returned'}
                </span>
              )}
            </div>
            
            {/* Product Title */}
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
            
            {/* Category */}
            <p className="text-gray-600 mb-4 capitalize">
              {product.category_info?.name || product.category} / {product.subcategory_info?.name || product.subcategory.replace(/-/g, ' ')}
            </p>
            
            {/* Rating */}
            {product.rating > 0 && (
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className="h-4 w-4" 
                      fill={i < Math.floor(product.rating) ? "currentColor" : "none"} 
                    />
                  ))}
                </div>
                <span className="ml-2 text-gray-500 text-sm">{product.rating.toFixed(1)} ({Math.floor(product.rating * 10)} reviews)</span>
              </div>
            )}
            
            {/* Price */}
            <div className="mb-6">
              <span className="text-2xl font-bold text-gray-900">
                {product.product_type === 'rental' ? `${formatPrice(product.price)}/month` : formatPrice(product.price)}
              </span>
            </div>
            
            {/* Short Description */}
            <p className="text-gray-700 mb-6">{product.description}</p>
            
            {/* Add to Cart */}
            {product.quantity > 0 && product.product_type === 'regular' && (
              <div className="mb-8 space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="flex border border-gray-300 rounded-md overflow-hidden">
                    <button 
                      className="px-3 py-1 bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                      onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                      disabled={quantity <= 1}
                    >
                      -
                    </button>
                    <input 
                      type="number" 
                      min="1"
                      max={product.quantity}
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                      className="w-12 text-center border-x border-gray-300"
                    />
                    <button 
                      className="px-3 py-1 bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                      onClick={() => setQuantity(prev => Math.min(product.quantity, prev + 1))}
                      disabled={quantity >= product.quantity}
                    >
                      +
                    </button>
                  </div>
                  
                  <button 
                    onClick={handleAddToCart}
                    className="btn btn-primary py-2 px-8 flex items-center space-x-2"
                  >
                    <ShoppingCart className="h-5 w-5" />
                    <span>Add to Cart</span>
                  </button>
                </div>
              </div>
            )}

            {/* Contact for Special Items */}
            {(product.product_type !== 'regular' || product.quantity === 0) && (
              <div className="mb-8">
                <Link 
                  to="/contact"
                  className="btn btn-secondary py-3 px-8 flex items-center justify-center space-x-2"
                >
                  <span>
                    {product.quantity === 0 ? 'Notify When Available' :
                     product.product_type === 'rental' ? 'Rent Now' : 'Contact Us'}
                  </span>
                </Link>
              </div>
            )}
            
            {/* Features */}
            <div className="mb-6 space-y-2">
              {[
                { icon: <Truck className="h-5 w-5 text-red-700" />, text: "Free shipping on orders over ₹9,999" },
                { icon: <Shield className="h-5 w-5 text-red-700" />, text: "2-year warranty included" },
              ].map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  {feature.icon}
                  <span className="text-gray-600 text-sm">{feature.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
        
        {/* Product Details Tabs */}
        <div className="mt-12 border-t border-gray-200 pt-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              {[
                { id: 'description', name: 'Description' },
                { id: 'specs', name: 'Specifications' },
                { id: 'features', name: 'Features' },
                { id: 'reviews', name: 'Reviews' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`pb-4 text-sm font-medium ${
                    activeTab === tab.id
                      ? 'border-b-2 border-red-500 text-red-600'
                      : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
          
          <div className="py-6">
            {activeTab === 'description' && (
              <div className="prose max-w-none">
                <p>{product.description}</p>
              </div>
            )}
            
            {activeTab === 'specs' && (
              <div className="border rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <tbody className="divide-y divide-gray-200">
                    {Object.entries(product.specifications || {}).map(([key, value]) => (
                      <tr key={key}>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50 w-1/3">
                          {key}
                        </td>
                        <td className="px-6 py-3 whitespace-normal text-sm text-gray-500">
                          {typeof value === 'string' ? value : JSON.stringify(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {activeTab === 'features' && (
              <ul className="list-disc pl-5 space-y-2">
                {product.features.map((feature, index) => (
                  <li key={index} className="text-gray-700">{feature}</li>
                ))}
              </ul>
            )}
            
            {activeTab === 'reviews' && (
              <ProductReviewsSection
                productId={product.id}
                productName={product.name}
              />
            )}
          </div>
        </div>
        
        {/* Related Products */}
        {filteredRelatedProducts.length > 0 && (
          <ProductRecommendations 
            currentProductId={product.id}
            category={product.category}
            title="You may also like"
            limit={4}
          />
        )}
      </div>

      {/* Image Modal */}
      {isImageModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={handleModalOverlayClick}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setIsImageModalOpen(false)}
              className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-colors z-10"
            >
              <X className="h-6 w-6" />
            </button>
            <div className="bg-white rounded-lg p-6 max-h-[90vh] overflow-auto">
              <img 
                src={productImages[selectedImage] || productImages[0] || getProductImageUrl(null)} 
                alt={product.name} 
                className="w-full h-auto max-h-[80vh] object-contain"
                onError={(e) => {
                  e.currentTarget.src = getProductImageUrl(null);
                }}
              />
              <div className="mt-4 text-center">
                <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
                <p className="text-gray-600 mt-1">Click outside to close</p>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Add to Cart Modal */}
      <AddToCartModal
        isOpen={showCartModal}
        onClose={() => setShowCartModal(false)}
        product={{
          id: product.id,
          name: product.name,
          price: product.price,
          image: getProductImageUrl(product.image_url),
          stock: product.quantity
        }}
        initialQuantity={quantity}
      />
    </div>
  );
};

export default ProductDetailPage;